import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DAMAGE_TYPES, TRADE_LABELS, TradeType } from "@/lib/damageTypes";

interface FieldScoutHUDProps {
  duration: number;
  bookmarkCount: number;
  selectedTrade: TradeType;
  onTradeSelect: (trade: TradeType) => void;
  selectedDamageType?: string;
  onDamageTypeSelect: (type: string) => void;
  onMarkDamage: () => void;
  hasGPS: boolean;
}

export const FieldScoutHUD = ({
  duration,
  bookmarkCount,
  selectedTrade,
  onTradeSelect,
  selectedDamageType,
  onDamageTypeSelect,
  onMarkDamage,
  hasGPS
}: FieldScoutHUDProps) => {
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const damageTypes = DAMAGE_TYPES[selectedTrade];

  return (
    <div className="absolute inset-0 pointer-events-none flex flex-col z-40">
      {/* Top Bar - with top padding to avoid overlap with controls */}
      <div className="bg-gradient-to-b from-transparent via-black/40 to-transparent pt-20 px-4 pb-4 pointer-events-auto space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-2xl font-mono text-white font-bold">
              {formatTime(duration)}
            </div>
            <div className="flex items-center gap-1">
              {hasGPS ? (
                <MapPin className="w-4 h-4 text-green-400" />
              ) : (
                <MapPin className="w-4 h-4 text-gray-400" />
              )}
            </div>
          </div>
          
          <Badge variant="destructive" className="animate-pulse">
            REC
          </Badge>
        </div>

        {/* Trade Selector */}
        <Select value={selectedTrade} onValueChange={(value) => onTradeSelect(value as TradeType)}>
          <SelectTrigger className="w-full bg-black/60 border-white/30 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(TRADE_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Damage Type Chips */}
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {damageTypes.map((type) => (
            <Button
              key={type.value}
              size="sm"
              variant={selectedDamageType === type.value ? "default" : "outline"}
              onClick={() => onDamageTypeSelect(type.value)}
              className={`text-xs font-bold transition-all whitespace-nowrap ${
                selectedDamageType === type.value 
                  ? 'scale-105 shadow-lg' 
                  : 'bg-black/40 border-white/30 text-white hover:bg-white/20'
              }`}
            >
              {type.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Spacer - pushes bottom bar to actual bottom */}
      <div className="flex-1 pointer-events-none" />

      {/* Bottom Bar with Mark Button */}
      <div className="bg-gradient-to-t from-black/80 via-black/60 to-transparent p-4 pointer-events-auto flex items-center justify-between gap-4">
        <div>
          <div className="text-white text-lg font-bold">
            {bookmarkCount} {bookmarkCount === 1 ? 'Spot' : 'Spots'} Marked
          </div>
          {selectedDamageType && (
            <div className="text-white/70 text-sm mt-1">
              {TRADE_LABELS[selectedTrade]}: {damageTypes.find(t => t.value === selectedDamageType)?.label || selectedDamageType}
            </div>
          )}
        </div>
        <button
          onClick={onMarkDamage}
          className="pointer-events-auto relative w-14 h-14 rounded-full bg-destructive hover:bg-destructive/90 transition-all shadow-xl flex items-center justify-center"
        >
          <span className="text-white font-bold text-xs text-center leading-tight">
            MARK
            <br />
            DAMAGE
          </span>
        </button>
      </div>
    </div>
  );
};
